import React, { useState, useEffect, useCallback } from "react";
import {
  Tree,
  Button,
  Input,
  Modal,
  Form,
  Typography,
  Space,
  Tag,
  Table,
  Breadcrumb,
  Card,
  Row,
  Col,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  FileOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  updateAssetCategory,
  deleteAssetCategory,
  getAssetCategories,
  getAssetsByCategory,
  createAssetCategory,
} from "../api/asset";
import dayjs from "dayjs";
import axios from "axios";

const { Title, Text } = Typography;

const LEVEL_COLOR = { 1: "blue", 2: "green", 3: "gold" };
const LEVEL_LABEL = { 1: "一级", 2: "二级", 3: "三级" };

// ─── 把后端返回的树形数据转成 Ant Design Tree 需要的格式 ─────────────────────
function transformTree(nodes) {
  return nodes.map((node) => ({
    key: node._id,
    title: node.name,
    level: node.level,
    parent: node.parent,
    raw: node,
    icon: node.children?.length ? (
      ({ expanded }) => (expanded ? <FolderOpenOutlined /> : <FolderOutlined />)
    ) : (
      <FileOutlined />
    ),
    children: node.children?.length ? transformTree(node.children) : undefined,
  }));
}

// ─── 平铺树形数据，用于路径计算 ───────────────────────────────────────────────
function flattenTree(nodes, result = []) {
  nodes.forEach((n) => {
    result.push(n);
    if (n.children?.length) flattenTree(n.children, result);
  });
  return result;
}

// ─── 新增/编辑弹窗 ────────────────────────────────────────────────────────────
function CategoryFormModal({
  open,
  mode,
  parentNode,
  editNode,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const isEdit = mode === "edit";
  const title = isEdit
    ? "编辑类别名称"
    : parentNode
      ? "添加子类别"
      : "新增顶级类别";

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (isEdit && editNode) {
        form.setFieldsValue({ name: editNode.raw.name });
      }
    }
  }, [open, mode, editNode]);

  const handleSubmit = async () => {
    try {
      const { name } = await form.validateFields();
      setLoading(true);
      console.log("change name : ", name);
      if (isEdit) {
        await updateAssetCategory(editNode.key, { name });
        // await axios.put(`/api/asset-categories/${editNode.key}`, { name });
        message.success("更新成功");
      } else {
        await createAssetCategory({ name, parent: parentNode?.key || null });
        // await axios.post("/api/asset-categories", {
        //   name,
        //   parent: parentNode?.key || null,
        // });
        message.success("创建成功");
      }
      onSuccess();
      onClose();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="保存"
      cancelText="取消"
      confirmLoading={loading}
      width={380}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {/* 父级提示 */}
        {!isEdit && (
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 12,
              color: "#64748b",
            }}
          >
            {parentNode ? (
              <>
                父级：<Text strong>{parentNode.title}</Text>（
                {LEVEL_LABEL[parentNode.level] || parentNode.level + "级"}）→
                将创建为{" "}
                <Text strong>
                  {LEVEL_LABEL[parentNode.level + 1] ||
                    parentNode.level + 1 + "级"}
                  类别
                </Text>
              </>
            ) : (
              <>
                不设父级，创建为 <Text strong>一级</Text> 分类
              </>
            )}
          </div>
        )}
        {isEdit && editNode && (
          <div
            style={{
              background: "#f8fafc",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 12,
              color: "#64748b",
            }}
          >
            层级不可修改（当前：
            <Text strong>
              {LEVEL_LABEL[editNode.level] || editNode.level + "级"}
            </Text>
            ）
          </div>
        )}
        <Form.Item
          name="name"
          label="类别名称"
          rules={[{ required: true, message: "请输入类别名称" }]}
        >
          <Input placeholder="请输入类别名称" autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── 主页面 ───────────────────────────────────────────────────────────────────
export default function AssetCategoryPage() {
  const [treeData, setTreeData] = useState([]);
  const [flatData, setFlatData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  // 弹窗
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add-root"); // 'add-root' | 'add-sub' | 'edit'
  const [parentNode, setParentNode] = useState(null);

  useEffect(() => {
    fetchTree();
  }, []);

  const fetchTree = async () => {
    setLoading(true);
    try {
      const { data } = await getAssetCategories();
      console.log("data:", data);
      const transformed = transformTree(data);
      setTreeData(transformed);
      setFlatData(flattenTree(transformed));
      // 默认展开一级
      setExpandedKeys(transformed.map((n) => n.key));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ── 选中树节点 ──────────────────────────────────────────────────────────────
  const handleSelect = (keys, { node }) => {
    if (!keys.length) return;
    setSelectedKeys(keys);
    setSelectedNode(node);
  };

  // ── 获取面包屑路径 ──────────────────────────────────────────────────────────
  const getBreadcrumb = useCallback(
    (node) => {
      if (!node) return [];
      const path = [node];
      let cur = node;
      while (cur.parent) {
        const p = flatData.find((n) => n.key === cur.parent);
        if (p) {
          path.unshift(p);
          cur = p;
        } else break;
      }
      return path;
    },
    [flatData],
  );

  // ── 删除 ────────────────────────────────────────────────────────────────────
  const handleDelete = async (node) => {
    Modal.confirm({
      title: `确认删除「${node.title}」？`,
      content: "删除后不可恢复，有子类别时无法删除。",
      okText: "删除",
      okType: "danger",
      cancelText: "取消",
      onOk: async () => {
        try {
          console.log("node:", node);
          await deleteAssetCategory(node.raw._id);
          message.success("删除成功");
          if (selectedNode?.key === node.key) {
            setSelectedNode(null);
            setSelectedKeys([]);
          }
          fetchTree();
        } catch (e) {
          message.error(e?.response?.data?.message || "删除失败");
        }
      },
    });
  };

  // ── 搜索过滤 ─────────────────────────────────────────────────────────────────
  const filteredTree = searchValue
    ? flatData
        .filter((n) => n.title.includes(searchValue))
        .map((n) => ({ ...n, children: undefined }))
    : treeData;

  // ── 子类别表格列 ────────────────────────────────────────────────────────────
  const subColumns = [
    {
      title: "名称",
      dataIndex: "title",
      key: "title",
      render: (v) => (
        <Text strong style={{ fontSize: 13 }}>
          {v}
        </Text>
      ),
    },
    {
      title: "层级",
      dataIndex: "level",
      key: "level",
      width: 80,
      render: (v) => (
        <Tag
          color={LEVEL_COLOR[v] || "default"}
          style={{ borderRadius: 4, fontSize: 11 }}
        >
          {LEVEL_LABEL[v] || v + "级"}
        </Tag>
      ),
    },
    {
      title: "子类别数",
      key: "childCount",
      width: 90,
      render: (_, r) => (
        <Text
          style={{ fontFamily: "monospace", fontSize: 12, color: "#64748b" }}
        >
          {r.children?.length || 0}
        </Text>
      ),
    },
    {
      title: "创建时间",
      key: "createdAt",
      width: 130,
      render: (_, r) => (
        <Text
          type="secondary"
          style={{ fontFamily: "monospace", fontSize: 11 }}
        >
          {r.raw?.createdAt ? dayjs(r.raw.createdAt).format("YYYY-MM-DD") : "—"}
        </Text>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 130,
      render: (_, r) => (
        <Space size={6}>
          <Button
            size="small"
            onClick={() => {
              setSelectedKeys([r.key]);
              setSelectedNode(r);
            }}
          >
            查看
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedNode(r);
              setModalMode("edit");
              setModalOpen(true);
            }}
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  const breadcrumb = getBreadcrumb(selectedNode);

  return (
    <div style={{ padding: 24, background: "#f5f6f8", minHeight: "100vh" }}>
      {/* 页面标题 */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            资产类别
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            分类管理 · 树形结构
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{
            background: "#1e293b",
            borderColor: "#1e293b",
            fontWeight: 500,
          }}
          onClick={() => {
            setModalMode("add-root");
            setParentNode(null);
            setModalOpen(true);
          }}
        >
          新增顶级类别
        </Button>
      </div>

      <Row gutter={16}>
        {/* 左侧：类别树 */}
        <Col span={7}>
          <Card
            style={{
              borderRadius: 10,
              border: "0.5px solid #e2e8f0",
              boxShadow: "none",
            }}
            bodyStyle={{ padding: 0 }}
            title={
              <Space>
                <Text
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    fontWeight: 500,
                    letterSpacing: "0.05em",
                  }}
                >
                  类别树
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    color: "#64748b",
                    background: "#f1f5f9",
                    padding: "1px 7px",
                    borderRadius: 4,
                  }}
                >
                  {flatData.length} 个
                </Text>
              </Space>
            }
          >
            {/* 搜索框 */}
            <div
              style={{
                padding: "10px 12px",
                borderBottom: "0.5px solid #f1f5f9",
              }}
            >
              <Input
                prefix={
                  <SearchOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
                }
                placeholder="搜索类别名称..."
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                allowClear
              />
            </div>
            {/* 树形组件 */}
            <div style={{ padding: "8px 4px" }}>
              <Tree
                showIcon
                blockNode
                treeData={filteredTree}
                selectedKeys={selectedKeys}
                expandedKeys={
                  searchValue ? flatData.map((n) => n.key) : expandedKeys
                }
                onExpand={(keys) => setExpandedKeys(keys)}
                onSelect={handleSelect}
                style={{ fontSize: 13 }}
              />
            </div>
          </Card>
        </Col>

        {/* 右侧：详情 */}
        <Col span={17}>
          <Card
            style={{
              borderRadius: 10,
              border: "0.5px solid #e2e8f0",
              boxShadow: "none",
              minHeight: 400,
            }}
            bodyStyle={{ padding: 0 }}
            title={
              <Text
                style={{
                  fontSize: 11,
                  color: "#94a3b8",
                  fontWeight: 500,
                  letterSpacing: "0.05em",
                }}
              >
                {selectedNode ? selectedNode.title : "请选择类别"}
              </Text>
            }
            extra={
              selectedNode && (
                <Space>
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{ background: "#1e293b", borderColor: "#1e293b" }}
                    onClick={() => {
                      setModalMode("add-sub");
                      setParentNode(selectedNode);
                      setModalOpen(true);
                    }}
                  >
                    添加子类别
                  </Button>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setModalMode("edit");
                      setModalOpen(true);
                    }}
                  >
                    编辑
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(selectedNode)}
                  >
                    删除
                  </Button>
                </Space>
              )
            }
          >
            {!selectedNode ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "80px 20px",
                  color: "#cbd5e1",
                }}
              >
                <FolderOutlined
                  style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}
                />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  从左侧选择一个类别查看详情
                </Text>
              </div>
            ) : (
              <>
                {/* 面包屑 + 当前类别信息 */}
                <div style={{ padding: "20px 24px 16px" }}>
                  <Breadcrumb
                    style={{ fontSize: 12, marginBottom: 12 }}
                    items={breadcrumb.map((p, i) => ({
                      title:
                        i === breadcrumb.length - 1 ? (
                          <Text strong style={{ fontSize: 12 }}>
                            {p.title}
                          </Text>
                        ) : (
                          <Text
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedKeys([p.key]);
                              setSelectedNode(p);
                            }}
                          >
                            {p.title}
                          </Text>
                        ),
                    }))}
                  />
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <Text
                      style={{
                        fontSize: 22,
                        fontWeight: 500,
                        color: "#1e293b",
                      }}
                    >
                      {selectedNode.title}
                    </Text>
                    <Tag
                      color={LEVEL_COLOR[selectedNode.level] || "default"}
                      style={{ borderRadius: 4 }}
                    >
                      {LEVEL_LABEL[selectedNode.level] ||
                        selectedNode.level + "级"}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      子类别 {selectedNode.children?.length || 0} 个
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      创建于{" "}
                      {selectedNode.raw?.createdAt
                        ? dayjs(selectedNode.raw.createdAt).format("YYYY-MM-DD")
                        : "—"}
                    </Text>
                  </div>
                </div>

                {/* 子类别列表 */}
                <div
                  style={{
                    borderTop: "0.5px solid #f1f5f9",
                    padding: "12px 24px 8px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      fontWeight: 500,
                      letterSpacing: "0.05em",
                    }}
                  >
                    子类别（{selectedNode.children?.length || 0}）
                  </Text>
                </div>
                <Table
                  dataSource={selectedNode.children || []}
                  columns={subColumns}
                  rowKey="key"
                  size="small"
                  pagination={false}
                  locale={{ emptyText: "暂无子类别" }}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* 新增/编辑弹窗 */}
      <CategoryFormModal
        open={modalOpen}
        mode={modalMode}
        parentNode={modalMode === "add-sub" ? selectedNode : parentNode}
        editNode={modalMode === "edit" ? selectedNode : null}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          fetchTree();
        }}
      />
    </div>
  );
}
